import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getProviderHeaders = () => {
  const token = localStorage.getItem('provider_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Family APIs
export const createFamily = async (name) => {
  const response = await axios.post(`${API}/family/create`, { name }, { headers: getAuthHeaders() });
  return response.data;
};

export const getFamily = async () => {
  const response = await axios.get(`${API}/family`, { headers: getAuthHeaders() });
  return response.data;
};

export const getFamilyMembers = async () => {
  const response = await axios.get(`${API}/family/members`, { headers: getAuthHeaders() });
  return response.data;
};

export const addFamilyMember = async (email, fullName, relationship) => {
  const response = await axios.post(`${API}/family/add-member`, 
    { email, full_name: fullName, relationship }, 
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const removeFamilyMember = async (memberId) => {
  const response = await axios.delete(`${API}/family/member/${memberId}`, { headers: getAuthHeaders() });
  return response.data;
};

// Health Records APIs
export const uploadHealthRecord = async (formData) => {
  const response = await axios.post(`${API}/records/upload`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getHealthRecords = async (memberId = null, recordType = null) => {
  const params = new URLSearchParams();
  if (memberId) params.append('member_id', memberId);
  if (recordType) params.append('record_type', recordType);
  
  const response = await axios.get(`${API}/records?${params.toString()}`, { headers: getAuthHeaders() });
  return response.data;
};

export const getHealthRecord = async (recordId) => {
  const response = await axios.get(`${API}/records/${recordId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const deleteHealthRecord = async (recordId) => {
  const response = await axios.delete(`${API}/records/${recordId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const createShareLink = async (recordId, expiresInHours) => {
  const response = await axios.post(
    `${API}/records/${recordId}/share`,
    { expires_in_hours: expiresInHours },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getSharedRecord = async (token) => {
  const response = await axios.get(`${API}/share/${token}`);
  return response.data;
};

// Analytics APIs
export const getHealthTrends = async (memberId = null, metricName = null) => {
  const params = new URLSearchParams();
  if (memberId) params.append('member_id', memberId);
  if (metricName) params.append('metric_name', metricName);
  
  const response = await axios.get(`${API}/analytics/trends?${params.toString()}`, { headers: getAuthHeaders() });
  return response.data;
};

export const getHealthSummary = async () => {
  const response = await axios.get(`${API}/analytics/summary`, { headers: getAuthHeaders() });
  return response.data;
};

// Reminders APIs
export const createReminder = async (title, description, reminderDate, reminderType) => {
  const response = await axios.post(`${API}/reminders`, 
    { title, description, reminder_date: reminderDate, reminder_type: reminderType },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getReminders = async () => {
  const response = await axios.get(`${API}/reminders`, { headers: getAuthHeaders() });
  return response.data;
};

export const completeReminder = async (reminderId) => {
  const response = await axios.put(`${API}/reminders/${reminderId}/complete`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const deleteReminder = async (reminderId) => {
  const response = await axios.delete(`${API}/reminders/${reminderId}`, { headers: getAuthHeaders() });
  return response.data;
};

// Doctor Notes APIs
export const createDoctorNote = async (title, content, doctorName) => {
  const response = await axios.post(`${API}/doctor-notes`, 
    { title, content, doctor_name: doctorName },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getDoctorNotes = async () => {
  const response = await axios.get(`${API}/doctor-notes`, { headers: getAuthHeaders() });
  return response.data;
};

export const deleteDoctorNote = async (noteId) => {
  const response = await axios.delete(`${API}/doctor-notes/${noteId}`, { headers: getAuthHeaders() });
  return response.data;
};

// AI Scribe APIs
export const generateAiScribeSummary = async (consultationNotes) => {
  const formData = new FormData();
  formData.append('consultation_notes', consultationNotes);
  
  const response = await axios.post(`${API}/ai-scribe/summarize`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getScribeHistory = async () => {
  const response = await axios.get(`${API}/ai-scribe/history`, { headers: getAuthHeaders() });
  return response.data;
};

// Prescriptions APIs
export const createPrescription = async (data) => {
  const response = await axios.post(`${API}/prescriptions`, data, { headers: getAuthHeaders() });
  return response.data;
};

export const getPrescriptions = async () => {
  const response = await axios.get(`${API}/prescriptions`, { headers: getAuthHeaders() });
  return response.data;
};

export const getPrescription = async (prescriptionId) => {
  const response = await axios.get(`${API}/prescriptions/${prescriptionId}`, { headers: getAuthHeaders() });
  return response.data;
};

// Certificates APIs
export const createCertificate = async (data) => {
  const response = await axios.post(`${API}/certificates`, data, { headers: getAuthHeaders() });
  return response.data;
};

export const getCertificates = async () => {
  const response = await axios.get(`${API}/certificates`, { headers: getAuthHeaders() });
  return response.data;
};

// Consultations APIs
export const createConsultation = async (title, scheduledTime, participants) => {
  const response = await axios.post(`${API}/consultations`, 
    { title, scheduled_time: scheduledTime, participants },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getConsultations = async () => {
  const response = await axios.get(`${API}/consultations`, { headers: getAuthHeaders() });
  return response.data;
};

export const getConsultation = async (consultationId) => {
  const response = await axios.get(`${API}/consultations/${consultationId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const updateConsultationNotes = async (consultationId, notes) => {
  const formData = new FormData();
  formData.append('notes', notes);
  
  const response = await axios.put(`${API}/consultations/${consultationId}/notes`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Provider APIs
export const providerRegister = async (payload) => {
  const response = await axios.post(`${API}/providers/register`, payload);
  return response.data;
};

export const providerLogin = async (email, password) => {
  const response = await axios.post(`${API}/providers/login`, { email, password });
  return response.data;
};

export const getProviderRequests = async () => {
  const response = await axios.get(`${API}/providers/requests`, { headers: getProviderHeaders() });
  return response.data;
};

export const createProviderRequest = async (payload) => {
  const response = await axios.post(`${API}/providers/requests`, payload, { headers: getProviderHeaders() });
  return response.data;
};

export const getProviderPatients = async () => {
  const response = await axios.get(`${API}/providers/patients`, { headers: getProviderHeaders() });
  return response.data;
};

export const getProviderPatientRecords = async (patientId) => {
  const response = await axios.get(`${API}/providers/patients/${patientId}/records`, { headers: getProviderHeaders() });
  return response.data;
};

export const runProviderAnalysis = async (payload) => {
  const response = await axios.post(`${API}/providers/patients/analysis`, payload, { headers: getProviderHeaders() });
  return response.data;
};

// Patient requests
export const getPatientRequests = async () => {
  const response = await axios.get(`${API}/patient/requests`, { headers: getAuthHeaders() });
  return response.data;
};

export const respondToPatientRequest = async (requestId, status, message = '') => {
  const response = await axios.post(
    `${API}/patient/requests/${requestId}/respond`,
    { status, message },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Integrations
export const getAbhaStatus = async () => {
  const response = await axios.get(`${API}/integrations/abha/status`, { headers: getAuthHeaders() });
  return response.data;
};

export const connectAbha = async () => {
  const response = await axios.post(`${API}/integrations/abha/connect`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const getUhrStatus = async () => {
  const response = await axios.get(`${API}/integrations/uhr/status`, { headers: getAuthHeaders() });
  return response.data;
};

export const connectUhr = async () => {
  const response = await axios.post(`${API}/integrations/uhr/connect`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Nearby hospitals (OpenStreetMap interim)
export const getNearbyHospitals = async (params) => {
  const response = await axios.get(`${API}/locations/hospitals`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

// Wearables
export const createWearableMetric = async (payload) => {
  const response = await axios.post(`${API}/wearables/metrics`, payload, { headers: getAuthHeaders() });
  return response.data;
};

export const getWearableMetrics = async (params = {}) => {
  const response = await axios.get(`${API}/wearables/metrics`, { params, headers: getAuthHeaders() });
  return response.data;
};

export const getWearableSummary = async () => {
  const response = await axios.get(`${API}/wearables/summary`, { headers: getAuthHeaders() });
  return response.data;
};

export const analyzeWearables = async (payload) => {
  const response = await axios.post(`${API}/wearables/analyze`, payload, { headers: getAuthHeaders() });
  return response.data;
};
